import { useRef, useState, ChangeEvent } from 'react';
import 'react-image-crop/dist/ReactCrop.css';
import i18n from '@/localisation/i18n';
import { ResponseUpdateAvatar, updateAvatar } from './api/updateAvatar';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { useMutation } from '@tanstack/react-query';
import { useUserStore } from '@/store/user';
import { resizeImageBlob } from './helper';

export default function ImageCropper() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const modalRef = useRef<HTMLDialogElement | null>(null);

  const [image, setImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { updatePsychologist, img } = useUserStore();

  const cropperRef = useRef<ReactCropperElement>(null);

  const { mutate } = useMutation({
    mutationFn: (blob: Blob) => updateAvatar(blob),
    onSuccess: (data: ResponseUpdateAvatar) => {
      updatePsychologist({ img: data.img });

      modalRef.current?.close();

      setTimeout(() => {
        setImage(null);
        setIsLoading(false);
      }, 1000);
    },
  });

  const onSelectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        setImage(reader.result as string);

        modalRef.current?.showModal();
      };
      reader.readAsDataURL(file);

      event.target.value = '';
    }
  };

  const onCrop = () => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper) {
      return;
    }

    cropper.getCroppedCanvas().toBlob((blob: Blob | null) => {
      if (!blob) {
        return;
      }

      resizeImageBlob(blob, 150, 150).then(blobNew => {
        mutate(blobNew);
      });
    }, 'image/webp');

    setIsLoading(true);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <button
        onClick={handleButtonClick}
        className="btn btn-xs border-additional bg-transparent"
      >
        {i18n.t(img ? 'upload.new' : 'upload')}
      </button>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        className="hidden"
        onChange={onSelectFile}
      />

      <dialog ref={modalRef} className="modal">
        <div className="modal-box bg-main w-auto min-w-96">
          <form method="dialog">
            <button className="btn btn-circle btn-ghost btn-sm absolute top-2 right-2">
              ✕
            </button>
          </form>
          <h3 className="text-lg font-bold">{i18n.t('add.photo')}</h3>
          {image && (
            <div className="mx-auto mt-4 min-h-[400px] w-[400px]">
              <Cropper
                src={image}
                className="max-h-[500px] w-96"
                aspectRatio={1}
                ref={cropperRef}
                viewMode={1}
                guides={false}
              />
            </div>
          )}
          <div className="flex justify-end">
            <button
              disabled={isLoading}
              className="btn btn-primary mt-4"
              onClick={onCrop}
            >
              {i18n.t('save')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
